import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  MapPin, 
  Truck, 
  Navigation, 
  Play, 
  Pause, 
  RotateCcw, 
  RefreshCw,
  Clock,
  Route,
  Gauge,
  Compass
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Custom truck icon
const truckIcon = new L.DivIcon({
  className: "custom-truck-icon",
  html: `<div style="background: #3b82f6; border-radius: 50%; padding: 8px; border: 2px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="2">
      <path d="M1 3h15v13H1z"/>
      <path d="M16 8h4l3 5v5h-7V8z"/>
      <circle cx="5.5" cy="18.5" r="2.5"/>
      <circle cx="18.5" cy="18.5" r="2.5"/>
    </svg>
  </div>`,
  iconSize: [40, 40],
  iconAnchor: [20, 40],
});

// Map center component
const MapCenter = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, map.getZoom());
    }
  }, [center, map]);
  return null;
};

const GPSTrackingPage = () => {
  const [activeTab, setActiveTab] = useState("live");
  const [liveVehicles, setLiveVehicles] = useState([]);
  const [trips, setTrips] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [simulateDialogOpen, setSimulateDialogOpen] = useState(false);
  const [tripDialogOpen, setTripDialogOpen] = useState(false);
  const [mapCenter, setMapCenter] = useState([20.5937, 78.9629]); // India center
  const [autoRefresh, setAutoRefresh] = useState(false);
  const refreshInterval = useRef(null);
  
  // Playback state
  const [playbackIndex, setPlaybackIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const playbackInterval = useRef(null);

  // Simulate form
  const [simulateForm, setSimulateForm] = useState({
    vehicle_number: "",
    latitude: "",
    longitude: "",
    speed: "0",
    direction: "0",
    status: "moving"
  });

  // Trip form
  const [tripForm, setTripForm] = useState({
    vehicle_number: "",
    driver_name: "",
    start_location: "",
    end_location: "",
    start_lat: "",
    start_lng: ""
  });

  useEffect(() => {
    fetchData();
    return () => {
      if (refreshInterval.current) clearInterval(refreshInterval.current);
      if (playbackInterval.current) clearInterval(playbackInterval.current);
    };
  }, []);

  useEffect(() => {
    if (autoRefresh) {
      refreshInterval.current = setInterval(fetchLiveLocations, 10000);
    } else {
      if (refreshInterval.current) clearInterval(refreshInterval.current);
    }
    return () => {
      if (refreshInterval.current) clearInterval(refreshInterval.current);
    };
  }, [autoRefresh]);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchLiveLocations(),
        fetchTrips(),
        fetchTrucks()
      ]);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLiveLocations = async () => {
    try {
      const response = await axios.get(`${API_URL}/gps/live`);
      setLiveVehicles(response.data);
    } catch (error) {
      console.error("Error fetching live locations:", error);
    }
  };

  const fetchTrips = async () => {
    try {
      const response = await axios.get(`${API_URL}/gps/trips`);
      setTrips(response.data);
    } catch (error) {
      console.error("Error fetching trips:", error);
    }
  };

  const fetchTrucks = async () => {
    try {
      const response = await axios.get(`${API_URL}/trucks`);
      setTrucks(response.data);
    } catch (error) {
      console.error("Error fetching trucks:", error);
    }
  };

  const handleSimulate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/gps/simulate`, null, {
        params: {
          vehicle_number: simulateForm.vehicle_number,
          latitude: parseFloat(simulateForm.latitude),
          longitude: parseFloat(simulateForm.longitude),
          speed: parseFloat(simulateForm.speed),
          direction: parseFloat(simulateForm.direction),
          status: simulateForm.status
        }
      });
      toast.success("Location updated successfully");
      setSimulateDialogOpen(false);
      fetchLiveLocations();
    } catch (error) {
      toast.error("Failed to update location");
    }
  };

  const handleCreateTrip = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/gps/trips`, {
        vehicle_number: tripForm.vehicle_number,
        driver_name: tripForm.driver_name,
        start_location: tripForm.start_location,
        end_location: tripForm.end_location,
        start_lat: parseFloat(tripForm.start_lat) || null,
        start_lng: parseFloat(tripForm.start_lng) || null,
        start_time: new Date().toISOString(),
        status: "active"
      });
      toast.success("Trip started successfully");
      setTripDialogOpen(false);
      fetchTrips();
    } catch (error) {
      toast.error("Failed to start trip");
    }
  };

  const handleCompleteTrip = async (tripId) => {
    try {
      const trip = trips.find(t => t.id === tripId);
      await axios.put(`${API_URL}/gps/trips/${tripId}/complete`, null, {
        params: {
          end_lat: trip?.end_lat || 0,
          end_lng: trip?.end_lng || 0
        }
      });
      toast.success("Trip completed");
      fetchTrips();
    } catch (error) {
      toast.error("Failed to complete trip");
    }
  };

  const viewTripRoute = (trip) => {
    setSelectedTrip(trip);
    setPlaybackIndex(0);
    setIsPlaying(false);
    if (trip.route_points?.length > 0) {
      setMapCenter([trip.route_points[0].lat, trip.route_points[0].lng]);
    } else if (trip.start_lat && trip.start_lng) {
      setMapCenter([trip.start_lat, trip.start_lng]);
    }
  };

  // Playback controls
  const startPlayback = () => {
    if (!selectedTrip?.route_points?.length) return;
    setIsPlaying(true);
    playbackInterval.current = setInterval(() => {
      setPlaybackIndex(prev => {
        if (prev >= selectedTrip.route_points.length - 1) {
          clearInterval(playbackInterval.current);
          setIsPlaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, 500);
  };

  const pausePlayback = () => {
    setIsPlaying(false);
    if (playbackInterval.current) clearInterval(playbackInterval.current);
  };

  const resetPlayback = () => {
    setPlaybackIndex(0);
    setIsPlaying(false);
    if (playbackInterval.current) clearInterval(playbackInterval.current);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "moving": return "bg-green-500";
      case "stopped": return "bg-yellow-500";
      case "idle": return "bg-orange-500";
      case "offline": return "bg-red-500";
      default: return "bg-gray-500";
    }
  };

  const formatSpeed = (speed) => {
    return speed ? `${speed.toFixed(1)} km/h` : "0 km/h";
  };

  const formatDirection = (direction) => {
    if (!direction) return "N";
    const directions = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
    const index = Math.round(direction / 45) % 8;
    return directions[index];
  };

  return (
    <div className="space-y-6" data-testid="gps-tracking-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold">GPS Tracking</h1>
          <p className="text-muted-foreground">Real-time vehicle tracking and trip history</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant={autoRefresh ? "default" : "outline"} 
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={autoRefresh ? "bg-green-600" : ""}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${autoRefresh ? "animate-spin" : ""}`} />
            {autoRefresh ? "Auto-Refresh ON" : "Auto-Refresh"}
          </Button>
          <Button onClick={() => setSimulateDialogOpen(true)} variant="outline" data-testid="simulate-location-btn">
            <MapPin className="w-4 h-4 mr-2" />
            Simulate Location
          </Button>
          <Button onClick={() => setTripDialogOpen(true)} className="bg-primary" data-testid="start-trip-btn">
            <Route className="w-4 h-4 mr-2" />
            Start Trip
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-secondary/50">
          <TabsTrigger value="live" data-testid="tab-live">
            <MapPin className="w-4 h-4 mr-2" />
            Live Tracking ({liveVehicles.length})
          </TabsTrigger>
          <TabsTrigger value="trips" data-testid="tab-trips">
            <Route className="w-4 h-4 mr-2" />
            Trip History ({trips.length})
          </TabsTrigger>
        </TabsList>

        {/* Live Tracking Tab */}
        <TabsContent value="live" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Map */}
            <Card className="lg:col-span-2 bg-card border-border">
              <CardContent className="p-0">
                <div className="h-[500px] rounded-lg overflow-hidden">
                  <MapContainer
                    center={mapCenter}
                    zoom={5}
                    style={{ height: "100%", width: "100%" }}
                    className="z-0"
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <MapCenter center={mapCenter} />
                    {liveVehicles.map((vehicle) => (
                      vehicle.last_location && (
                        <Marker
                          key={vehicle.id}
                          position={[vehicle.last_location.latitude, vehicle.last_location.longitude]}
                          icon={truckIcon}
                        >
                          <Popup>
                            <div className="p-2">
                              <h3 className="font-bold text-lg">{vehicle.truck_number}</h3>
                              <p className="text-sm text-gray-600">{vehicle.truck_type}</p>
                              <div className="mt-2 space-y-1 text-sm">
                                <p><strong>Speed:</strong> {formatSpeed(vehicle.last_location.speed)}</p>
                                <p><strong>Direction:</strong> {formatDirection(vehicle.last_location.direction)}</p>
                                <p><strong>Status:</strong> {vehicle.last_location.status}</p>
                                {vehicle.last_location.address && (
                                  <p><strong>Address:</strong> {vehicle.last_location.address}</p>
                                )}
                                <p className="text-xs text-gray-500">
                                  Updated: {new Date(vehicle.last_location.timestamp).toLocaleString()}
                                </p>
                              </div>
                            </div>
                          </Popup>
                        </Marker>
                      )
                    ))}
                  </MapContainer>
                </div>
              </CardContent>
            </Card>

            {/* Vehicle List */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="w-5 h-5" />
                  Live Vehicles
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 max-h-[440px] overflow-y-auto">
                {liveVehicles.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    No vehicles with location data
                  </p>
                ) : (
                  liveVehicles.map((vehicle) => (
                    <div
                      key={vehicle.id}
                      className="p-3 bg-secondary/30 rounded-lg cursor-pointer hover:bg-secondary/50 transition-colors"
                      onClick={() => vehicle.last_location && setMapCenter([vehicle.last_location.latitude, vehicle.last_location.longitude])}
                      data-testid={`vehicle-card-${vehicle.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{vehicle.truck_number}</p>
                          <p className="text-xs text-muted-foreground">{vehicle.truck_type}</p>
                        </div>
                        {vehicle.last_location && (
                          <div className={`w-3 h-3 rounded-full ${getStatusColor(vehicle.last_location.status)}`} />
                        )}
                      </div>
                      {vehicle.last_location && (
                        <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                          <div className="flex items-center gap-1">
                            <Gauge className="w-3 h-3" />
                            {formatSpeed(vehicle.last_location.speed)}
                          </div>
                          <div className="flex items-center gap-1">
                            <Compass className="w-3 h-3" />
                            {formatDirection(vehicle.last_location.direction)}
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Trip History Tab */}
        <TabsContent value="trips" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Trip Route Map */}
            <Card className="lg:col-span-2 bg-card border-border">
              <CardHeader>
                <CardTitle>
                  {selectedTrip ? `Route: ${selectedTrip.vehicle_number}` : "Select a trip to view route"}
                </CardTitle>
                {selectedTrip?.route_points?.length > 0 && (
                  <div className="flex items-center gap-2 mt-2">
                    <Button size="sm" variant="outline" onClick={isPlaying ? pausePlayback : startPlayback}>
                      {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    </Button>
                    <Button size="sm" variant="outline" onClick={resetPlayback}>
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Point {playbackIndex + 1} / {selectedTrip.route_points.length}
                    </span>
                  </div>
                )}
              </CardHeader>
              <CardContent className="p-0">
                <div className="h-[400px] rounded-lg overflow-hidden">
                  <MapContainer
                    center={mapCenter}
                    zoom={10}
                    style={{ height: "100%", width: "100%" }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <MapCenter center={mapCenter} />
                    {selectedTrip?.route_points?.length > 0 && (
                      <>
                        <Polyline
                          positions={selectedTrip.route_points.slice(0, playbackIndex + 1).map(p => [p.lat, p.lng])}
                          color="#3b82f6"
                          weight={4}
                        />
                        <Marker
                          position={[
                            selectedTrip.route_points[playbackIndex].lat,
                            selectedTrip.route_points[playbackIndex].lng
                          ]}
                          icon={truckIcon}
                        >
                          <Popup>
                            <div className="p-2">
                              <p><strong>Speed:</strong> {formatSpeed(selectedTrip.route_points[playbackIndex].speed)}</p>
                              <p><strong>Time:</strong> {new Date(selectedTrip.route_points[playbackIndex].timestamp).toLocaleString()}</p>
                            </div>
                          </Popup>
                        </Marker>
                      </>
                    )}
                  </MapContainer>
                </div>
              </CardContent>
            </Card>

            {/* Trip List */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Route className="w-5 h-5" />
                  Trips
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 max-h-[400px] overflow-y-auto">
                {trips.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    No trips recorded
                  </p>
                ) : (
                  trips.map((trip) => (
                    <div
                      key={trip.id}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedTrip?.id === trip.id 
                          ? "bg-primary/20 border border-primary/50" 
                          : "bg-secondary/30 hover:bg-secondary/50"
                      }`}
                      onClick={() => viewTripRoute(trip)}
                      data-testid={`trip-card-${trip.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <p className="font-medium">{trip.vehicle_number}</p>
                        <Badge variant="outline" className={trip.status === "active" ? "bg-green-500/20 text-green-400" : "bg-gray-500/20"}>
                          {trip.status}
                        </Badge>
                      </div>
                      <div className="mt-2 text-xs text-muted-foreground">
                        <p>{trip.start_location} → {trip.end_location}</p>
                        <p className="flex items-center gap-1 mt-1">
                          <Clock className="w-3 h-3" />
                          {new Date(trip.start_time).toLocaleString()}
                        </p>
                        {trip.route_points?.length > 0 && (
                          <p className="text-primary mt-1">{trip.route_points.length} route points</p>
                        )}
                      </div>
                      {trip.status === "active" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-2 w-full"
                          onClick={(e) => { e.stopPropagation(); handleCompleteTrip(trip.id); }}
                        >
                          Complete Trip
                        </Button>
                      )}
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Simulate Location Dialog */}
      <Dialog open={simulateDialogOpen} onOpenChange={setSimulateDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Simulate GPS Location</DialogTitle>
            <DialogDescription>Manually update vehicle location for testing</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSimulate} className="space-y-4">
            <div className="space-y-2">
              <Label>Vehicle Number *</Label>
              <Select value={simulateForm.vehicle_number} onValueChange={(v) => setSimulateForm({...simulateForm, vehicle_number: v})}>
                <SelectTrigger className="bg-secondary/50">
                  <SelectValue placeholder="Select vehicle" />
                </SelectTrigger>
                <SelectContent>
                  {trucks.map((truck) => (
                    <SelectItem key={truck.id} value={truck.truck_number}>{truck.truck_number}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Latitude *</Label>
                <Input
                  type="number"
                  step="any"
                  value={simulateForm.latitude}
                  onChange={(e) => setSimulateForm({...simulateForm, latitude: e.target.value})}
                  placeholder="20.5937"
                  className="bg-secondary/50"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Longitude *</Label>
                <Input
                  type="number"
                  step="any"
                  value={simulateForm.longitude}
                  onChange={(e) => setSimulateForm({...simulateForm, longitude: e.target.value})}
                  placeholder="78.9629"
                  className="bg-secondary/50"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Speed (km/h)</Label>
                <Input
                  type="number"
                  value={simulateForm.speed}
                  onChange={(e) => setSimulateForm({...simulateForm, speed: e.target.value})}
                  className="bg-secondary/50"
                />
              </div>
              <div className="space-y-2">
                <Label>Direction (°)</Label>
                <Input
                  type="number"
                  min="0"
                  max="360"
                  value={simulateForm.direction}
                  onChange={(e) => setSimulateForm({...simulateForm, direction: e.target.value})}
                  className="bg-secondary/50"
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={simulateForm.status} onValueChange={(v) => setSimulateForm({...simulateForm, status: v})}>
                  <SelectTrigger className="bg-secondary/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="moving">Moving</SelectItem>
                    <SelectItem value="stopped">Stopped</SelectItem>
                    <SelectItem value="idle">Idle</SelectItem>
                    <SelectItem value="offline">Offline</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSimulateDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-primary">Update Location</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Start Trip Dialog */}
      <Dialog open={tripDialogOpen} onOpenChange={setTripDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Start New Trip</DialogTitle>
            <DialogDescription>Create a new trip for tracking</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateTrip} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Vehicle Number *</Label>
                <Select value={tripForm.vehicle_number} onValueChange={(v) => setTripForm({...tripForm, vehicle_number: v})}>
                  <SelectTrigger className="bg-secondary/50">
                    <SelectValue placeholder="Select vehicle" />
                  </SelectTrigger>
                  <SelectContent>
                    {trucks.map((truck) => (
                      <SelectItem key={truck.id} value={truck.truck_number}>{truck.truck_number}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Driver Name</Label>
                <Input
                  value={tripForm.driver_name}
                  onChange={(e) => setTripForm({...tripForm, driver_name: e.target.value})}
                  className="bg-secondary/50"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Location *</Label>
                <Input
                  value={tripForm.start_location}
                  onChange={(e) => setTripForm({...tripForm, start_location: e.target.value})}
                  placeholder="Mumbai"
                  className="bg-secondary/50"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>End Location *</Label>
                <Input
                  value={tripForm.end_location}
                  onChange={(e) => setTripForm({...tripForm, end_location: e.target.value})}
                  placeholder="Delhi"
                  className="bg-secondary/50"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Latitude</Label>
                <Input
                  type="number"
                  step="any"
                  value={tripForm.start_lat}
                  onChange={(e) => setTripForm({...tripForm, start_lat: e.target.value})}
                  placeholder="Optional"
                  className="bg-secondary/50"
                />
              </div>
              <div className="space-y-2">
                <Label>Start Longitude</Label>
                <Input
                  type="number"
                  step="any"
                  value={tripForm.start_lng}
                  onChange={(e) => setTripForm({...tripForm, start_lng: e.target.value})}
                  placeholder="Optional"
                  className="bg-secondary/50"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTripDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-primary">Start Trip</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GPSTrackingPage;
